import React, { useState, useEffect } from 'react';
import ProductCard from '../components/ui/ProductCard';
import { ProductCardSkeleton } from '../components/ui/Skeleton';
import { products } from '../data/mockData';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const Shop = () => {
  const [filter, setFilter] = useState('All');
  const [isLoading, setIsLoading] = useState(true);
  const filters = ['All', 'Living Room', 'Bedroom', 'Office', 'Calm', 'Vibrant'];

  // Simulate network request for data fetching/filtering
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 600);
    return () => clearTimeout(timer);
  }, [filter]);

  const filteredProducts = filter === 'All' 
    ? products 
    : products.filter(p => p.room === filter || p.mood === filter);

  return (
    <div className="container mx-auto px-6 md:px-12 py-16 animate-fade-in">
      <header className="mb-16 md:mb-20">
        <div className="flex flex-col md:flex-row justify-between items-baseline gap-8">
          <div className="max-w-2xl">
            <h1 className="text-5xl md:text-7xl font-serif mb-6 leading-tight">The Portfolio</h1>
            <p className="text-gray-600 text-lg md:text-xl leading-relaxed">
              Explore our curated selection of limited edition prints. Each piece is hand-inspected and custom-framed in our local foundry.
            </p>
          </div>
          <div className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.2em] text-gray-500" aria-live="polite">
            <SafeIcon icon={FiIcons.FiFilter} /> 
            {isLoading ? 'Updating...' : `${filteredProducts.length} Works Available`}
          </div>
        </div>
      </header>

      {/* Filter Bar */}
      <nav aria-label="Portfolio filters" className="flex flex-wrap gap-3 mb-16 pb-4 border-b border-gray-100">
        {filters.map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            aria-pressed={filter === f}
            className={`px-6 md:px-8 py-3 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] transition-all duration-300 min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine focus-visible:ring-offset-2 ${
              filter === f 
                ? 'bg-ultramarine text-white shadow-lg' 
                : 'bg-gray-50 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f}
          </button>
        ))}
      </nav>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-12 gap-y-20 md:gap-y-24" aria-live="polite" aria-busy={isLoading}>
        <AnimatePresence mode="wait">
          {isLoading ? (
            // Loading State
            Array.from({ length: 8 }).map((_, idx) => (
              <motion.div
                key={`skeleton-${idx}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <ProductCardSkeleton />
              </motion.div>
            ))
          ) : filteredProducts.length > 0 ? (
            // Success State
            filteredProducts.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))
          ) : (
            // Empty State
            <motion.div 
              key="empty-state"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="col-span-full text-center py-32 md:py-40"
            >
              <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-8 text-gray-400">
                <SafeIcon icon={FiIcons.FiImage} className="text-4xl" />
              </div>
              <h3 className="text-2xl md:text-3xl font-serif mb-4 text-charcoal">No matches found</h3>
              <p className="text-gray-500 text-base max-w-md mx-auto mb-8">We couldn't find any pieces matching your current filters. Try adjusting your selection to discover more art.</p>
              <button 
                onClick={() => setFilter('All')}
                className="text-ultramarine font-bold uppercase tracking-widest text-[11px] border-b-2 border-ultramarine pb-1 hover:text-charcoal hover:border-charcoal transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine focus-visible:ring-offset-4 rounded-sm"
              >
                Clear All Filters
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Shop;